import React, { useMemo } from "react";
import dayjs from "dayjs";

import { useCatalog } from "../../../contexts/CatalogContext";
import { Button } from "../../../components/ui/button";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { useNotification } from "../../../contexts/NotificationContext";
import { Download } from "lucide-react";

interface Props {
  rawData: any[];
}

export default function BookingTable({ rawData }: Props) {
  const { driversMap, vehiclesMap } = useCatalog();
  const { showNotification } = useNotification();

  const rows = useMemo(() => {
    return rawData.map((booking: any) => {
      const driver = driversMap[booking.driver];
      const vehicle = vehiclesMap[booking.vehicle];
      const passengersName = (() => {
        if (!booking.passengersQty || booking.passengersQty === 1) return booking.name;
        const extra = [] as string[];
        for (let i = 2; i <= booking.passengersQty; i++) {
          if (booking[`passenger${i}`]) {
            extra.push(booking[`passenger${i}`]);
          }
        }
        return `${booking.name}${extra.length ? `, ${extra.join(", ")}` : ""}`;
      })();

      const paymentMethod = (() => {
        switch (booking.paymentMethod) {
          case "pix":
            return "PIX";
          case "card":
            return "Cartão";
          default:
            return "Dinheiro";
        }
      })();

      const currency = (() => {
        switch (booking.currency) {
          case "dollar":
            return "$";
          case "euro":
            return "€";
          case "pesos":
            return "₱";
          default:
            return "R$";
        }
      })();

      return {
        ...booking,
        driverName: driver ? driver.name : "N/A",
        vehicleInfo: vehicle ? `${vehicle.name} (${vehicle.plate || ""})` : "N/A",
        passengersName,
        paymentMethod,
        currency,
      };
    });
  }, [rawData, driversMap, vehiclesMap]);

  const exportToCSV = () => {
    const headers = [
      "Data",
      "Nome",
      "Valor",
      "Meio de pagamento",
      "Telefone",
      "Motorista",
      "Veículo",
      "Qtd. Passageiros",
      "Passageiros",
      "Origem",
      "Destino",
      "Voo",
      "Horário",
      "Observações",
    ];

    showNotification("info", "Baixando CSV...");

    const csvRows = rows.map((booking) => {
      const sanitize = (value: any) =>
        value !== undefined && value !== null ? String(value).trim() : "N/A";

      return [
        sanitize(dayjs(booking.date).format("DD/MM/YYYY")),
        sanitize(booking.name),
        sanitize(`${booking.currency} ${booking.value}`),
        sanitize(booking.paymentMethod),
        sanitize(booking.phone),
        sanitize(booking.driverName),
        sanitize(booking.vehicleInfo),
        sanitize(booking.passengersQty),
        sanitize(booking.passengersName),
        sanitize(booking.origin),
        sanitize(booking.destination),
        sanitize(booking.flight || "N/A"),
        sanitize(dayjs(booking.date).format("HH:mm")),
        sanitize(booking.observation || "N/A"),
      ];
    });

    const csvContent = [headers, ...csvRows]
      .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `agendamentos-${dayjs().format("DD-MM-YYYY")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showNotification("success", "Arquivo CSV baixado com sucesso!");
  };

  return (
    <div className="space-y-4">
      <Button onClick={exportToCSV} className="w-full">
        <Download className="mr-2 h-4 w-4" /> Exportar CSV
      </Button>
      <Table>
        <TableCaption>Visão em tabela das reservas filtradas.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Data</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Valor</TableHead>
            <TableHead>Pagamento</TableHead>
            <TableHead>Telefone</TableHead>
            <TableHead>Motorista</TableHead>
            <TableHead>Veículo</TableHead>
            <TableHead>Qtd.</TableHead>
            <TableHead>Passageiros</TableHead>
            <TableHead>Origem</TableHead>
            <TableHead>Destino</TableHead>
            <TableHead>Voo</TableHead>
            <TableHead>Hora</TableHead>
            <TableHead>Observações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((booking) => (
            <TableRow key={booking.id}>
              <TableCell>{dayjs(booking.date).format("DD/MM/YYYY")}</TableCell>
              <TableCell>{booking.name}</TableCell>
              <TableCell>
                {booking.currency} {booking.value}
              </TableCell>
              <TableCell>{booking.paymentMethod}</TableCell>
              <TableCell>{booking.phone}</TableCell>
              <TableCell>{booking.driverName}</TableCell>
              <TableCell>{booking.vehicleInfo}</TableCell>
              <TableCell>{booking.passengersQty}</TableCell>
              <TableCell>{booking.passengersName}</TableCell>
              <TableCell>{booking.origin}</TableCell>
              <TableCell>{booking.destination}</TableCell>
              <TableCell>{booking.flight || "N/A"}</TableCell>
              <TableCell>{dayjs(booking.date).format("HH:mm")}</TableCell>
              <TableCell>{booking.observation || "N/A"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
